import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { gameStore } from '../lib/game-store';
import {
  DEFAULT_ROOM_CONFIG,
  type GameStateSnapshot,
  type PlayerId,
  type RoomConfig,
} from '@juegazo/shared';
import './background';
import './game-config';
import './voice-controls';
import type { VoiceManager } from '../lib/voice-manager';

export const LOBBY_RECONNECT_TIMEOUT_MS = 8000;
export const LOBBY_RECONNECT_HARD_TIMEOUT_MS = 20000;

@customElement('jgz-lobby')
export class JgzLobby extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String }) roomId = '';
  @property({ type: Object }) state: GameStateSnapshot | null = null;
  @property({ type: String }) playerId: PlayerId | null = null;
  @property({ attribute: false }) voiceManager: VoiceManager | null = null;
  @property({ type: Object }) lastError: { code: string; message: string } | null = null;
  @state() private copied = false;
  @state() private playersSheetOpen = false;
  @state() private showEscape = false;
  private softTimer: number | null = null;
  private hardTimer: number | null = null;
  private autoLeft = false;

  connectedCallback(): void {
    super.connectedCallback();
    if (this.lastError) {
      this.showEscape = true;
      return;
    }
    this.softTimer = window.setTimeout(() => {
      this.softTimer = null;
      if (!this.state && !this.lastError) this.showEscape = true;
    }, LOBBY_RECONNECT_TIMEOUT_MS);
    this.hardTimer = window.setTimeout(() => {
      this.hardTimer = null;
      if (!this.state && !this.autoLeft) {
        this.autoLeft = true;
        this.dispatchEvent(new CustomEvent('jgz-leave', { bubbles: true, composed: true }));
      }
    }, LOBBY_RECONNECT_HARD_TIMEOUT_MS);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.softTimer !== null) {
      clearTimeout(this.softTimer);
      this.softTimer = null;
    }
    if (this.hardTimer !== null) {
      clearTimeout(this.hardTimer);
      this.hardTimer = null;
    }
  }

  private escape = () => {
    this.dispatchEvent(new CustomEvent('jgz-leave', { bubbles: true, composed: true }));
  };

  willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('state') && this.state) {
      if (this.softTimer !== null) {
        clearTimeout(this.softTimer);
        this.softTimer = null;
      }
      if (this.hardTimer !== null) {
        clearTimeout(this.hardTimer);
        this.hardTimer = null;
      }
    }
    if (changed.has('lastError') && this.lastError) {
      this.showEscape = true;
      if (this.softTimer !== null) {
        clearTimeout(this.softTimer);
        this.softTimer = null;
      }
    }
  }

  updated(changed: Map<string, unknown>): void {
    void changed;
  }

  private get config(): RoomConfig {
    const s = this.state;
    return {
      totalBombs: s?.totalBombs ?? DEFAULT_ROOM_CONFIG.totalBombs,
      enabledMechanics: s?.enabledMechanics?.length ? [...s.enabledMechanics] : [...DEFAULT_ROOM_CONFIG.enabledMechanics],
      mixedMode: s?.mixedMode ?? DEFAULT_ROOM_CONFIG.mixedMode,
      baseDurationMs: s?.baseDurationMs ?? DEFAULT_ROOM_CONFIG.baseDurationMs,
    };
  }

  private get isAdmin(): boolean {
    const s = this.state;
    if (!s || !this.playerId) return false;
    const me = s.players.find((p) => p.id === this.playerId);
    return me?.isAdmin ?? false;
  }

  private get voicePeerCount(): number {
    return this.state?.players.filter((p) => p.voice?.enabled).length ?? 0;
  }

  private copyCode = async () => {
    try {
      await navigator.clipboard.writeText(this.roomId);
      this.copied = true;
      setTimeout(() => (this.copied = false), 1500);
    } catch {
      // ignore
    }
  };

  private startGame = () => {
    gameStore.send('room:start');
  };

  private leave = () => {
    gameStore.send('room:leave');
    this.dispatchEvent(new CustomEvent('jgz-leave', { bubbles: true, composed: true }));
  };

  private onConfigChange = (e: CustomEvent<RoomConfig>) => {
    if (!e.detail) return;
    gameStore.send('room:configure', { config: e.detail });
  };

  private renderPlayerMic(playerId: PlayerId) {
    if (playerId === this.playerId && this.voiceManager) {
      return html`<jgz-voice-controls .voiceManager=${this.voiceManager}></jgz-voice-controls>`;
    }
    const player = this.state?.players.find((p) => p.id === playerId);
    if (!player?.voice?.enabled) return nothing;
    return html`<span class="voice-peer-indicator" aria-label="Con voz" title="Con voz">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
        <path d="M19 10v1a7 7 0 0 1-14 0v-1" fill="none" stroke="currentColor" stroke-width="2"/>
        <line x1="12" y1="19" x2="12" y2="22" stroke="currentColor" stroke-width="2"/>
      </svg>
    </span>`;
  }

  render() {
if (!this.state) {
      const errorMessage = this.lastError?.code === 'reconnect_failed' || this.lastError?.code === 'reconnect_timeout'
        ? this.lastError.message
        : null;
      return html`
        <jgz-background></jgz-background>
        <div class="container fade-in" style="max-width: 480px; margin: 0 auto; position: relative; z-index: 1;">
          <div class="card" style="text-align: center;">
            <div style="font-size: 3.5rem;">${errorMessage ? '⚠️' : '🔌'}</div>
            <p>${errorMessage ?? 'Conectando con la sala…'}</p>
            ${this.showEscape
                ? html`
                    <div class="row" style="justify-content: center; margin-top: 1rem;">
                      <button
                        class="btn-pill primary"
                        data-testid="lobby-escape"
                        @click=${this.escape}
                        style="flex: none; width: auto; min-width: 200px;"
                      >
                        <span class="label">VOLVER AL INICIO</span>
                      </button>
                    </div>
                  `
                : nothing}
          </div>
        </div>
      `;
    }
    const playerCount = this.state.players.length;
    const voiceCount = this.voicePeerCount;
    const canStart =
      this.isAdmin && playerCount >= 2 && !this.state.players.some((p) => !p.isConnected);
    return html`
      <jgz-background></jgz-background>
      <div class="container fade-in" style="position: relative; z-index: 1;">
        <div class="stack" style="gap: 0.5rem;">
          <div class="row" style="justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.75rem;">
            <div class="juegazo-logo-wrap">
              <div class="juegazo-logo" style="font-family: 'Bungee', 'Impact', var(--font); letter-spacing: 0.04em; background: linear-gradient(135deg, var(--yellow) 0%, var(--orange) 100%); -webkit-background-clip: text; background-clip: text; color: transparent; line-height: 1; font-weight: 900;">¡JUEGAZO!</div>
            </div>
            <button class="icon-btn-circle primary" @click=${this.leave}>Salir</button>
          </div>
          <div class="card">
            <h3 style="margin: 0 0 0.25rem;">Código de sala</h3>
            <div class="room-code-display" style="font-size: 1.6rem; font-weight: 800; letter-spacing: 0.2em; text-align: center; padding: 0.3rem 0; color: var(--yellow);">${this.roomId}</div>
            <button class="chip copy-code-btn" @click=${this.copyCode} style="width: 100%; justify-content: center; margin-top: 0.3rem;">
              ${this.copied ? '✓ Copiado' : '📋 Copiar código'}
            </button>
          </div>
          <div class="card">
            <div class="row" style="align-items: center; gap: 0.6rem; margin-bottom: 0.6rem;">
              <div
                style="font-size: 0.78rem; letter-spacing: 0.18em; font-weight: 700; color: var(--muted);"
              >
                JUGADORES
              </div>
              ${voiceCount > 0
                ? html`<span class="voice-count-badge" data-testid="voice-peer-count" style="margin-left: auto;">${voiceCount} con voz</span>`
                : nothing}
            </div>
            <div
              class="players-summary"
              @click=${() => (this.playersSheetOpen = true)}
              role="button"
              tabindex="0"
            >
              <div class="players-summary-text">
                <span>${playerCount} ${playerCount === 1 ? 'Jugador conectado' : `Jugadores (${playerCount}/12)`}</span>
                <span class="muted" style="font-size: 0.9rem;">Toca para ver la lista</span>
              </div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 10l5 5 5-5"/></svg>
            </div>
          </div>
          ${this.playersSheetOpen ? this.renderPlayersSheet() : nothing}
          ${this.isAdmin
            ? html`
                <div class="card lobby-config-card">
                  <h3 style="margin: 0 0 0.25rem;">Configuración de la partida</h3>
                  <jgz-game-config .config=${this.config} @change=${this.onConfigChange}></jgz-game-config>
                </div>
                <button class="btn-pill primary" ?disabled=${!canStart} @click=${this.startGame}>
                  <span class="label">INICIAR PARTIDA</span>
                </button>
              `
            : html`
                <div class="card" style="text-align: center;">
                  <p class="muted" style="margin: 0; display: inline-flex; align-items: center; gap: 0.6rem; justify-content: center;">
                    <span class="loading-spinner" aria-hidden="true"></span>
                    <span>Esperando que el admin inicie la partida…</span>
                  </p>
                </div>
              `}
        </div>
      </div>
    `;
  }

  private renderPlayersSheet() {
    return html`
      <div class="bottom-sheet-backdrop" @click=${() => (this.playersSheetOpen = false)}></div>
      <div class="bottom-sheet" role="dialog" aria-label="Jugadores conectados">
        <div class="bottom-sheet-handle"></div>
        <div class="bottom-sheet-header">
          <span class="bottom-sheet-title">Jugadores (${this.state!.players.length}/12)</span>
          <button class="bottom-sheet-close" @click=${() => (this.playersSheetOpen = false)} aria-label="Cerrar">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="bottom-sheet-content">
          <div class="players-grid">
            ${this.state!.players.map(
              (p) => html`
                <div class="player-chip">
                  <span class="player-chip-name">${p.displayName}</span>
                  ${p.isAdmin ? html`<span class="player-chip-badge">Admin</span>` : nothing}
                  ${!p.isConnected ? html`<span class="player-chip-badge" style="background: rgba(255, 118, 117, 0.18); color: var(--orange-2);">Desconectado</span>` : nothing}
                  ${this.renderPlayerMic(p.id)}
                </div>
              `,
            )}
          </div>
        </div>
        <div class="bottom-sheet-footer">
          <button class="btn-pill primary" style="width: 100%;" @click=${() => (this.playersSheetOpen = false)}>
            <span class="label">LISTO</span>
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'jgz-lobby': JgzLobby;
  }
}